import { DEFAULT_PAGE_SIZE } from '@/lib/constants/pagination';
import { ApiErrorCode, withApiErrorHandler } from '@/modules/common/api/errors';
import { DropdownSearchItem } from '@/modules/common/components/molecules/selectors/dropdown';
import { CreatedEntity } from '@/modules/common/types/created-entity';
import { PaginatedResponse } from '@/modules/common/types/pagination-types';
import { axiosInstance } from '@/utils/axios';

import { mapTimeOffBalancePreviewFromDTO } from '../mappers/time-off-balance-preview.mapper';
import { mapTimeOffBalanceFromDTO } from '../mappers/time-off-balance.mapper';
import { mapTimeOffRequestDetailsFromDTO } from '../mappers/time-off-request-details.mapper';
import { mapTimeOffRequestFromDTO } from '../mappers/time-off-request.mapper';
import { mapReviewDecisionToDTO } from '../mappers/time-off-review.mapper';
import { CreateTimeOffRequestRequest } from '../types/dto/create-time-off-request.dto';
import {
  PreviewTimeOffRequestBalanceRequest,
  PreviewTimeOffRequestBalanceResponse,
} from '../types/dto/preview-time-off-balance.dto';
import {
  FindTimeOffApproversRequest,
  SuggestedApproverResponse,
} from '../types/dto/time-off-approvers.dto';
import { TimeOffBalanceSummaryItemResponse } from '../types/dto/time-off-balance.dto';
import { TimeOffRequestDetailsResponse } from '../types/dto/time-off-request-details.dto';
import { TimeOffRequestListItemResponse } from '../types/dto/time-off-request.dto';
import { ReviewTimeOffRequestParams } from '../types/dto/time-off-review.dto';
import { WorkingDayResponse } from '../types/dto/time-off-working-days.dto';
import { UpdateTimeOffRequestRequest } from '../types/dto/update-time-off-request.dto';
import { TimeOffBalance } from '../types/time-off-balance';
import { TimeOffBalancePreview } from '../types/time-off-balance-preview';
import { TimeOffRequest } from '../types/time-off-request';
import { TimeOffRequestDetails } from '../types/time-off-request-details';
import { TimeOffStatus } from '../types/time-off-status';
import { TimeOffTimeCategory } from '../types/time-off-time-category';

/** Maps a suggested-approver DTO to the dropdown item shape. */
const mapApproverToDropdownItem = (approver: SuggestedApproverResponse): DropdownSearchItem => ({
  id: String(approver.id),
  name: approver.name,
  avatarLink: approver.profileImageUrl ?? undefined,
});

export interface TimeOffRequestsFilter {
  typeIds?: number[];
  statuses?: TimeOffStatus[];
  timeCategory?: TimeOffTimeCategory;
  dateRange?: { from: string; to: string };
  requesterIds?: number[];
}

export interface GetTimeOffRequestsParams {
  filter: TimeOffRequestsFilter;
  pagination: { cursor: number | null; pageSize: number };
}

export const getTimeOffBalances = async (): Promise<TimeOffBalance[]> => {
  const response = await withApiErrorHandler(
    '/api/time-off/balances',
    ApiErrorCode.GENERIC_ERROR,
    () => axiosInstance.get<{ data: TimeOffBalanceSummaryItemResponse[] }>('/api/time-off/balances')
  );

  return response.map(mapTimeOffBalanceFromDTO);
};

/**
 * Fetches full details of a single time-off request.
 *
 * Backend caveat: the endpoint is documented as "for approver" and does not
 * return attachment metadata or balance data — those are sourced/derived
 * elsewhere. See REQUEST-DETAILS-MISSING-API.md.
 */
export const getTimeOffRequestDetails = async (id: number): Promise<TimeOffRequestDetails> => {
  const endpoint = `/api/time-off/requests/${id}/details`;

  const response = await withApiErrorHandler(endpoint, ApiErrorCode.GENERIC_ERROR, () =>
    axiosInstance.get<{ data: TimeOffRequestDetailsResponse }>(endpoint)
  );

  return mapTimeOffRequestDetailsFromDTO(response);
};

/**
 * Fetches the current user's balance for a single time-off type. Used by the
 * request details screen to render the "Your balance" card (the details
 * endpoint itself carries no balance data).
 */
export const getTimeOffBalanceByType = async (typeId: number): Promise<TimeOffBalance> => {
  const endpoint = `/api/time-off/balances/type/${typeId}`;

  const response = await withApiErrorHandler(endpoint, ApiErrorCode.GENERIC_ERROR, () =>
    axiosInstance.get<{ data: TimeOffBalanceSummaryItemResponse }>(endpoint)
  );

  return mapTimeOffBalanceFromDTO(response);
};

export const getTimeOffRequests = async (
  params: GetTimeOffRequestsParams
): Promise<PaginatedResponse<TimeOffRequest>> => {
  const endpoint = '/api/time-off/requests/search';

  const response = await withApiErrorHandler(endpoint, ApiErrorCode.GENERIC_ERROR, () =>
    axiosInstance.post<{ data: { items: TimeOffRequestListItemResponse[]; pagination: any } }>(
      endpoint,
      params
    )
  );

  return new PaginatedResponse({
    items: response.items.map(mapTimeOffRequestFromDTO),
    pagination: response.pagination,
  });
};

export const getTimeOffRequestsToReview = async (
  params: GetTimeOffRequestsParams
): Promise<PaginatedResponse<TimeOffRequest>> => {
  const response = await withApiErrorHandler(
    '/api/me/time-off/requests/to-review/search',
    ApiErrorCode.GENERIC_ERROR,
    () =>
      axiosInstance.post<{ data: { items: TimeOffRequestListItemResponse[]; pagination: any } }>(
        '/api/me/time-off/requests/to-review/search',
        params
      )
  );

  return new PaginatedResponse({
    items: response.items.map(mapTimeOffRequestFromDTO),
    pagination: response.pagination,
  });
};

/**
 * Approves or declines a time-off request the current user is an approver for.
 * The response payload is ignored — callers refetch the to-review list on success.
 */
export const reviewTimeOffRequest = async ({
  requestId,
  decision,
  comment,
}: ReviewTimeOffRequestParams): Promise<{ success: true }> => {
  const endpoint = '/api/time-off/requests/review';

  await withApiErrorHandler(endpoint, ApiErrorCode.GENERIC_ERROR, () =>
    axiosInstance.put(endpoint, {
      requestId,
      decision: mapReviewDecisionToDTO(decision),
      // Omit the field entirely when empty so the backend treats it as absent.
      ...(comment?.trim() ? { comment: comment.trim() } : {}),
    })
  );

  return { success: true };
};

export const cancelTimeOffRequest = async (id: number): Promise<{ success: true }> => {
  await withApiErrorHandler(`/api/time-off/requests/cancel`, ApiErrorCode.GENERIC_ERROR, () =>
    axiosInstance.put(`/api/time-off/requests/cancel`, {
      requestId: id,
    })
  );

  return { success: true };
};

/**
 * Creates a time-off request. Returns the created entity id; callers refetch the
 * list on success.
 */
export const createTimeOffRequest = async (
  payload: CreateTimeOffRequestRequest
): Promise<CreatedEntity> => {
  const endpoint = '/api/time-off/requests';

  const response = await withApiErrorHandler(endpoint, ApiErrorCode.GENERIC_ERROR, () =>
    axiosInstance.post<{ data: CreatedEntity }>(endpoint, payload)
  );

  return response;
};

/**
 * Previews a not-yet-submitted request's balance impact, using the exact same
 * renewal-aware projection the backend uses to accept/reject the real submission.
 */
export const previewTimeOffRequestBalance = async (
  payload: PreviewTimeOffRequestBalanceRequest
): Promise<TimeOffBalancePreview> => {
  const endpoint = '/api/time-off/requests/balance-preview';

  const response = await withApiErrorHandler(endpoint, ApiErrorCode.GENERIC_ERROR, () =>
    axiosInstance.post<{ data: PreviewTimeOffRequestBalanceResponse }>(endpoint, payload)
  );

  return mapTimeOffBalancePreviewFromDTO(response);
};

export const updateTimeOffRequest = async (
  payload: UpdateTimeOffRequestRequest
): Promise<{ success: true }> => {
  const endpoint = '/api/time-off/requests/update';

  await withApiErrorHandler(endpoint, ApiErrorCode.GENERIC_ERROR, () =>
    axiosInstance.put(endpoint, payload)
  );

  return { success: true };
};

/**
 * Fetches working/non-working days across a date range. Drives the create-mode
 * day list (non-working days render disabled and are excluded from the payload).
 */
export const getWorkingDays = async (params: {
  startDate: string;
  endDate: string;
}): Promise<WorkingDayResponse[]> => {
  const endpoint = '/api/time-off/working-days';

  const response = await withApiErrorHandler(endpoint, ApiErrorCode.GENERIC_ERROR, () =>
    axiosInstance.get<{ data: WorkingDayResponse[] }>(endpoint, { params })
  );

  return response;
};

/** Fetches suggested approvers to prefill the create-mode approvers table. */
export const getSuggestedApprovers = async (): Promise<DropdownSearchItem[]> => {
  const endpoint = '/api/time-off/requests/suggested-approvers';

  const response = await withApiErrorHandler(endpoint, ApiErrorCode.GENERIC_ERROR, () =>
    axiosInstance.get<{ data: SuggestedApproverResponse[] }>(endpoint)
  );

  return response.map(mapApproverToDropdownItem);
};

/** Searches users who can approve time-off requests — the "Add approver" dropdown source. */
export const searchApprovers = async (
  searchString?: string,
  cursor?: number | null
): Promise<PaginatedResponse<DropdownSearchItem>> => {
  const endpoint = '/api/time-off/requests/approvers/search';

  const body: FindTimeOffApproversRequest = {
    searchString: searchString || undefined,
    pagination: { pageSize: DEFAULT_PAGE_SIZE, cursor: cursor ?? null },
  };

  const response = await withApiErrorHandler(endpoint, ApiErrorCode.GENERIC_ERROR, () =>
    axiosInstance.post<{
      data: { items: SuggestedApproverResponse[]; pagination: any };
    }>(endpoint, body)
  );

  return new PaginatedResponse({
    items: response.items.map(mapApproverToDropdownItem),
    // Normalize a missing/undefined cursor to `null` so the paginated dropdown's
    // `cursor !== null` "has more" check is correct (an undefined cursor would
    // otherwise read as "more pages" and reload page 1).
    pagination: {
      ...response.pagination,
      cursor: response.pagination?.cursor ?? null,
    },
  });
};

/**
 * Downloads the file attached to a time-off request.
 *
 * NOT IMPLEMENTED on the backend yet — neither the details response nor any
 * dedicated endpoint exposes attachments (see REQUEST-DETAILS-MISSING-API.md).
 * The signature is final-shaped so wiring it up later is a body-only change.
 *
 * @param requestId - The time-off request id.
 * @param attachmentId - The attachment id (shape TBD once the API lands).
 */
export const downloadTimeOffAttachment = async (
  _requestId: number,
  _attachmentId: number | string
): Promise<Blob> => {
  // TODO(time-off-attachments): replace with the real endpoint once the backend ready
  // exposes attachment download (e.g. GET /api/time-off/requests/:id/attachments/:attachmentId).
  throw new Error('TIME_OFF_ATTACHMENT_DOWNLOAD_NOT_IMPLEMENTED');
};
